import React, { useMemo } from "react";
import { Table } from "antd";
import { UserOutlined, FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { COMMON_DICTS, getMetadataLabel } from "@ifa/utils";
import { useFormConfig } from "../../../hooks/useFormConfig";
import styles from "./PersonView.module.css";

interface PersonViewProps {
  personData: any;
  currentSidebarStep: string;
  formRequiredConfig: any;
}

export function PersonView({ personData, currentSidebarStep, formRequiredConfig }: PersonViewProps) {
  // Determine form key based on current sidebar step
  const formKey = useMemo(() => {
    if (currentSidebarStep === "policyholder2") {
      return "SecondPolicyholderForm";
    } else if (currentSidebarStep === "insured2") {
      return "SecondInsuredForm";
    } else if (currentSidebarStep === "insured") {
      return "InsuredForm";
    } else if (currentSidebarStep === "policyholder") {
      return "PolicyholderForm";
    }
    return "";
  }, [currentSidebarStep]);

  const { isVisible } = useFormConfig(formKey, formRequiredConfig, personData);

  // Refined Schema for Person Info
  const getPersonSchema = (personData: any) => {
    const idType = getMetadataLabel("ID_TYPE", personData?.basic?.idType, COMMON_DICTS.idType);
    const idNumber = personData?.basic?.idNumber;

    return [
      {
        label: idType || "身份證明文件",
        key: "basic.idNumber",
        render: () => idNumber || "-",
      },
      {
        label: "姓名(中文)",
        key: "fullChineseName",
        render: () => `${personData?.basic?.chineseSurname || ""}${personData?.basic?.chineseGivenName || ""}`,
      },
      { label: "名字(中文)", key: "basic.chineseGivenName" },
      { label: "姓氏(英文)", key: "basic.englishSurname" },
      { label: "名字(英文)", key: "basic.englishGivenName" },
      {
        label: "出生日期",
        key: "basic.birthDate",
        render: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
      },
      {
        label: "年齡",
        key: "age",
        render: () => {
          if (!personData?.basic?.birthDate) return "-";
          return dayjs().diff(dayjs(personData.basic.birthDate), "year");
        },
      },
      {
        label: "性別",
        key: "basic.gender",
        render: (v: any) => getMetadataLabel("GENDER", v, COMMON_DICTS.gender),
      },
      {
        label: "稱謂",
        key: "basic.salutation",
        render: (v: any) => getMetadataLabel("SALUTATION", v, COMMON_DICTS.salutation),
      },
    ];
  };

  const contactSchema = [
    {
      label: "手提電話",
      key: "personal.phone",
      render: (v: any) => (v ? `+${personData?.personal?.phoneAreaCode || ""} ${v}` : "-"),
    },
    { label: "電郵", key: "personal.email" },
    {
      label: "通訊地址",
      key: "personal.address",
      render: (v: any) => {
        const country = getMetadataLabel("COUNTRY", personData?.personal?.country, COMMON_DICTS.country);
        const address = v || "";
        if (!country && !address) return "-";
        if (!country) return address;
        if (!address) return country;
        return `${country} ${address}`;
      },
    },
    { label: "身高(CM)", key: "personal.height" },
    { label: "體重(KG)", key: "personal.weight" },
    {
      label: "是否吸煙",
      key: "personal.smoke",
      render: (v: any) => getMetadataLabel("SMOKE", v, COMMON_DICTS.boolean),
    },
    {
      label: "教育程度",
      key: "personal.education",
      render: (v: any) => getMetadataLabel("EDUCATION", v, COMMON_DICTS.education),
    },
    {
      label: "婚姻狀況",
      key: "personal.marriage",
      render: (v: any) => getMetadataLabel("MARRIAGE_STATUS", v, COMMON_DICTS.maritalStatus),
    },
  ].filter((item) => {
    // Hide marital status for Insured
    if (currentSidebarStep.includes("insured") && item.key === "personal.marriage") return false;
    return true;
  });

  // Work schema with visibility filtering
  const workSchema = useMemo(() => {
    const allWorkFields = [
      {
        label: "就業類型",
        key: "work.type",
        fieldKey: "work.type",
        render: (v: any) => getMetadataLabel("WORK_INFO_EMPLOYMENT_TYPE", v, COMMON_DICTS.jobType),
      },
      { label: "僱主(公司)名稱", key: "work.employer.name", fieldKey: "work.employer.name" },
      { label: "僱主(公司)地址", key: "work.employer.address", fieldKey: "work.employer.address" },
      {
        label: "業務性質(行業)",
        key: "work.employer.industry",
        fieldKey: "work.employer.industry",
        render: (v: any) => getMetadataLabel("WORK_INFO_EMPLOYER_INDUSTRY", v, []),
      },
      { label: "職位及日常職務", key: "work.jobTitle", fieldKey: "work.jobTitle" },
      {
        label: "預計退休年齡",
        key: "work.intendedRetirementAge",
        fieldKey: "work.intendedRetirementAge",
        render: (v: any) => getMetadataLabel("INTENDED_RETIREMENT_AGE_OPTION", v, []),
      },
      { label: "預計退休年齡其他說明", key: "work.intendedRetirementAgeOther", fieldKey: "work.intendedRetirementAgeOther" },
    ];

    return allWorkFields.filter((item) => {
      // First check visibility from config
      if (!isVisible(item.fieldKey)) return false;

      // For Insured, only show employmentType and jobTitle
      if (currentSidebarStep.includes("insured")) {
        return ["work.employmentType", "work.jobTitle", "work.type"].includes(item.key);
      }
      return true;
    });
  }, [isVisible, currentSidebarStep]);

  const getTaxSchema = (personData: any) => {
    const baseSchema = [
      {
        label: "國籍(國家/地區)",
        key: "tax.nationality",
        render: (v: any) => getMetadataLabel("COUNTRY", v, COMMON_DICTS.country),
      },
      {
        label: "出生國家",
        key: "tax.countryOfBirth",
        render: (v: any) => getMetadataLabel("COUNTRY", v, COMMON_DICTS.country),
      },
      {
        label: "入境香港證件類型",
        key: "tax.hkEntryIdType",
        render: (v: any) => getMetadataLabel("HK_ENTRY_ID_TYPE", v, COMMON_DICTS.hkEntryIdType),
      },
      { label: "入境香港證件號碼", key: "tax.hkEntryIdNumber" },
      {
        label: "是否有其他稅務居民身份",
        key: "tax.hasOtherTaxResidency",
        render: (v: any) => getMetadataLabel("WHETHER", v, COMMON_DICTS.boolean),
      },
    ];

    // If hasOtherTaxResidency is true, add tax country and tax code fields
    if (personData?.tax?.hasOtherTaxResidency == "YES") {
      baseSchema.push(
        {
          label: "稅務國家",
          key: "tax.taxCountry",
          render: (v: any) => getMetadataLabel("COUNTRY", v, COMMON_DICTS.country),
        },
        {
          label: "稅務編號",
          key: "tax.taxCode",
        }
      );
    }

    baseSchema.push(
      {
        label: "是否擁有美國公民身份",
        key: "tax.isUSCitizen",
        render: (v: any) => getMetadataLabel("WHETHER", v, null),
      },
      {
        label: "是否有香港身份證",
        key: "tax.hasHKIDCard",
        render: (v: any) => getMetadataLabel("WHETHER", v, null),
      },
      {
        label: "是否是香港永久居民",
        key: "tax.isHKPermanentResident",
        render: (v: any) => getMetadataLabel("WHETHER", v, null),
      }
    );

    return baseSchema;
  };

  // Custom InfoCard component
  const InfoCard = ({ title, children, extra }: { title: string; children?: React.ReactNode; extra?: React.ReactNode }) => (
    <div className={styles["info-card"]}>
      <div className={styles["card-header"]}>
        <h3 className={styles["card-title"]}>{title}</h3>
        {extra && <div className={styles["card-extra"]}>{extra}</div>}
      </div>
      {children && <div className={styles["card-body"]}>{children}</div>}
    </div>
  );

  // Custom InfoField component
  const InfoField = ({ label, value, span = 1 }: { label: string; value: React.ReactNode; span?: 1 | 2 }) => (
    <div className={`${styles["info-field"]} ${span === 2 ? styles["span-2"] : ""}`}>
      <div className={styles["field-label"]}>{label}</div>
      <div className={styles["field-value"]}>{value || "-"}</div>
    </div>
  );

  const renderFields = (
    data: any,
    schema: {
      label: string;
      key: string;
      render?: (val: any) => React.ReactNode;
    }[]
  ) => {
    if (!data)
      return (
        <div className={styles["empty-state"]}>
          <FileTextOutlined style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }} />
          <div>無資料</div>
        </div>
      );

    return (
      <div className={styles["fields-grid"]}>
        {schema.map((item) => {
          const val = item.key.split(".").reduce((o, i) => (o ? o[i] : null), data);
          return <InfoField key={item.key} label={item.label} value={item.render ? item.render(val) : val} />;
        })}
      </div>
    );
  };

  if (!personData)
    return (
      <div className={styles["empty-state"]}>
        <UserOutlined style={{ fontSize: 64, color: "#d9d9d9", marginBottom: 16 }} />
        <div>無資料</div>
      </div>
    );

  const isSecondPolicyholder = currentSidebarStep === "policyholder2";
  const isInsured = currentSidebarStep === "insured";
  const isSecondInsured = currentSidebarStep === "insured2";
  const isSimplifiedForm = isSecondPolicyholder || isSecondInsured;
  const isPrimaryInsured = isInsured;

  // Relationship logic
  const hasRelationshipSection = isInsured || isSecondPolicyholder || isSecondInsured;
  const isSelf = personData.relationship === "SELF";

  // Dynamic Section Numbering
  const getSectionTitle = (baseTitle: string, index: number) => {
    let num = index;
    if (hasRelationshipSection) num += 1;
    return `${String(num).padStart(2, "0")} ${baseTitle}`;
  };

  // Special Case: Relationship is SELF
  const renderBasicInfoOrSelfMessage = () => {
    if (isSelf) {
      return (
        <div className={styles["self-message"]}>
          <UserOutlined style={{ fontSize: 32, color: "#00C7B6", marginBottom: 12 }} />
          <p>
            {isSecondPolicyholder
              ? "第二投保人與受保人關係為本人，詳細資料將自動與受保人保持一致，無需展示。"
              : isInsured
              ? "受保人與投保人關係為本人，詳細資料將自動與受保人保持一致，無需展示。"
              : "關係為本人，詳細資料自動保持一致。"}
          </p>
        </div>
      );
    }

    return <InfoCard title={getSectionTitle(isInsured || isSecondInsured ? "受保人基本資料" : "投保人基本資料", 1)}>{renderFields(personData, getPersonSchema(personData))}</InfoCard>;
  };

  return (
    <div className={styles["detail-content"]}>
      {/* Relationship - Only for Insured/Second Policyholder/Second Insured */}
      {hasRelationshipSection && (
        <InfoCard title="01 與投保人關係" extra={<span className={styles["relationship-value"]}>{getMetadataLabel(null, personData.relationship, COMMON_DICTS.relationship)}</span>} />
      )}

      {/* Basic Info or Self Message */}
      {renderBasicInfoOrSelfMessage()}

      {/* Personal Info - Hidden for Simplified Form */}
      {!isSimplifiedForm && !isSelf && <InfoCard title={getSectionTitle(isInsured || isSecondInsured ? "受保人個人資料" : "投保人個人資料", 2)}>{renderFields(personData, contactSchema)}</InfoCard>}

      {/* Work Info - Hidden for Simplified Form */}
      {!isSimplifiedForm && !isSelf && <InfoCard title={getSectionTitle(isInsured || isSecondInsured ? "受保人工作資料" : "投保人工作資料", 3)}>{renderFields(personData, workSchema)}</InfoCard>}

      {/* Tax Info - Hidden for Simplified Form */}
      {!isSimplifiedForm && !isSelf && (
        <InfoCard title={getSectionTitle(isInsured || isSecondInsured ? "受保人國籍、稅務等資料" : "投保人國籍、稅務等資料", 4)}>{renderFields(personData, getTaxSchema(personData))}</InfoCard>
      )}

      {/* Family Members - Hidden for Simplified Form AND Primary Insured */}
      {!isSimplifiedForm && !isSelf && !isPrimaryInsured && personData.familyMembers && personData.familyMembers.length > 0 && (
        <InfoCard title={getSectionTitle("投保人家庭成員", 5)}>
          <Table
            dataSource={personData.familyMembers}
            pagination={false}
            rowKey="id"
            className={styles["family-table"]}
            columns={[
              {
                title: "成員",
                dataIndex: "name",
                key: "name",
                render: (_, __, idx) => `成員${idx + 1}`,
              },
              { title: "成員年齡", dataIndex: "age", key: "age" },
              {
                title: "成員關係",
                dataIndex: "relationship",
                key: "relationship",
                render: (v) => getMetadataLabel("FAMILY_MEMBER_RELATIONSHIP", v, COMMON_DICTS.relationship),
              },
              {
                title: "是否需要供養",
                dataIndex: "isDependent",
                key: "isDependent",
                render: (v) => getMetadataLabel("WHETHER", v, COMMON_DICTS.boolean),
              },
            ]}
          />
        </InfoCard>
      )}

      {/* Other Info - Hidden for Simplified Form (Second Policyholder & Second Insured) and Insured */}
      {!isInsured && !isSimplifiedForm && (
        <InfoCard title={getSectionTitle("其他資料", !isPrimaryInsured ? 6 : 5)}>
          <div className={styles["fields-grid"]}>
            <InfoField label="是否專業投資者/PI" value={personData.other?.isProfessionalInvestor ?? personData.isProfessionalInvestor ? "是" : "否"} />
            <InfoField label="是否重點關注客戶" value={personData.other?.isVulnerableCustomer ?? personData.isVulnerableCustomer ? "是" : "否"} />
            {personData.other?.isVulnerableCustomer && (
              <>
                <InfoField label="見證人姓名" value={personData.other.witnessName} />
                <InfoField label="見證人證件類型" value={getMetadataLabel("ID_TYPE", personData.other.witnessIdType, COMMON_DICTS.idType)} />
                <InfoField label="見證人證件號碼" value={personData.other.witnessIdNumber || "-"} span={2} />
              </>
            )}
            <InfoField label="備註/其他說明" value={personData.other?.remark ?? personData.remark ?? "-"} span={2} />
          </div>
        </InfoCard>
      )}
    </div>
  );
}
